/**
 * useLiveClosing — la sortie du live de l'élève, et ce qui la suit hors du live : le quiz de fin de
 * séance (`live-quiz`), puis, quand le tuteur a clôturé, le rapport (`live-end`).
 *
 * La machine du live (`studentLiveMachine`) dit ce que la fin fait d'une sortie qu'elle croise
 * (`onEnded`, `left` ou non) ; ce crochet y place le quiz :
 *  - Le tuteur clôture, l'élève encore dans la salle (`onEnded`, pas `left`) : le quiz se demande, et
 *    le message « Ton masteur a clôturé le live » se pose quand il est arrivé — au plus tard après
 *    `CLOSED_QUIZ_WAIT_MS` : il annonce le quiz, ou félicite. Son bouton quitte le live comme
 *    « Quitter le live » : la note d'abord.
 *  - L'élève sort d'une séance close (`onEnded` avec `left` : le bouton du message, ou sa sortie qui
 *    croise la clôture) : le quiz, puis le rapport ; sans quiz, le rapport.
 *  - L'élève part avant, par la note (donnée ou passée) : à la sortie (`onOut`), le quiz se demande et
 *    son écran se pousse ; sans quiz, il rend l'accueil. Une sortie sans la note — dans le premier
 *    quart d'heure de la séance, ou en renonçant à se connecter — n'est pas une fin de séance : rien
 *    n'est proposé, la suite habituelle.
 *  - La séance rouverte avant que l'élève sorte : le message tombe, il revient au live (`popTo` :
 *    `navigate` empilerait un second écran du live, React Navigation 7).
 *
 * Les écrans qui suivent se poussent au geste de sortie, le live encore là le temps qu'il s'éteigne
 * (comme `live-end`) : leurs routes existent hors du live, la pile qui bascule les garde. La séance et
 * l'activité en pause leur passent en paramètres : la sortie du live les efface du magasin. Le quiz ne
 * se joue jamais dans la salle : la caméra y resterait allumée sans que l'élève la voie.
 */
import { useNavigation } from "@react-navigation/native"
import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { requestEndOfLiveQuiz, type ApiEndOfLiveQuiz } from "../../../../../../engine/live/student/endOfLiveQuiz"
import { type LiveExit } from "../../../../../../engine/live/student/useLiveLeave"
import { type EngineTelemetry } from "../../../../../../engine/shared/telemetry"

/** Le message de clôture attend le quiz au plus ce délai : au-delà, il se pose sans l'annoncer. */
export const CLOSED_QUIZ_WAIT_MS = 1500

type Navigation = {
    navigate: (route: string, params?: object) => void
    popTo: (route: string) => void
}

export type LiveClosingOptions = {
    /** La séance du live. */
    liveSessionId: number | null
    /** Une activité est en pause : le rapport la propose. Lu au moment d'y aller. */
    activityPaused?: () => boolean
}

export type LiveClosing = {
    onEnded: (exit: { left: boolean }) => void
    onReopened: () => void
    onOut: (exit: LiveExit) => void
    /** Le message de clôture à poser, `null` sinon. `quizMinutes` absent : pas de quiz. */
    dialog: { quizMinutes?: number } | null
}

export function useLiveClosing(telemetry: EngineTelemetry, options: LiveClosingOptions): LiveClosing {
    const { liveSessionId } = options
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation
    const [closed, setClosed] = useState(false)
    /** Le quiz de la clôture : `undefined` tant qu'il se demande. */
    const [quiz, setQuiz] = useState<ApiEndOfLiveQuiz | null | undefined>(undefined)
    const [waited, setWaited] = useState(false)
    // Close dès `onEnded`, pas au rendu suivant : une sortie qui la suit de près ne repart pas vers l'accueil,
    // et la demande en cours, à son retour, sait si la séance a été rouverte entre-temps.
    const closedRef = useRef(closed)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(
        () => () => {
            if (timerRef.current != null) clearTimeout(timerRef.current)
        },
        []
    )

    const next = () => ({ liveSessionId, activityPaused: options.activityPaused?.() ?? false })

    const onEnded = ({ left }: { left: boolean }) => {
        if (liveSessionId == null) return
        if (!closedRef.current) {
            closedRef.current = true
            setClosed(true)
            setQuiz(undefined)
            setWaited(false)
            void requestEndOfLiveQuiz(dispatch as never, liveSessionId, telemetry).then((served) => {
                if (closedRef.current) setQuiz(served)
            })
            timerRef.current = setTimeout(() => setWaited(true), CLOSED_QUIZ_WAIT_MS)
        }
        if (!left) return
        // Sortie d'une séance close : le quiz, arrivé ou en route (son écran le relit), puis le rapport.
        if (closed && quiz === null) navigation.navigate("live-end", { ...next(), left: true })
        else navigation.navigate("live-quiz", { ...next(), exitTo: "report" })
    }

    const onReopened = () => {
        if (timerRef.current != null) clearTimeout(timerRef.current)
        closedRef.current = false
        setClosed(false)
        navigation.popTo("menu")
    }

    const onOut = ({ afterRating }: LiveExit) => {
        // Une séance close mène au quiz par la machine (`onEnded` avec `left`).
        if (liveSessionId == null || closedRef.current || !afterRating) return
        void requestEndOfLiveQuiz(dispatch as never, liveSessionId, telemetry)
        navigation.navigate("live-quiz", { ...next(), exitTo: "home" })
    }

    const minutes = quiz?.minutes
    return {
        onEnded,
        onReopened,
        onOut,
        dialog:
            closed && (quiz !== undefined || waited) ? { quizMinutes: (minutes ?? 0) > 0 ? minutes : undefined } : null,
    }
}
